import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import CourseCard from "../components/common/CourseCard";
import { CourseDetailSkeleton } from "../components/common/PublicPageSkeletons";
import { useAuth } from "../contexts/AuthContext";
import { useCourseDetail } from "../hooks/useCourses";

const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { course, relatedCourses, loading, error } = useCourseDetail(id || "");

  const handleApplyClick = () => {
    if (!isAuthenticated) {
      navigate(`/signin?redirect=/apply/${id}`);
    } else {
      navigate(`/apply/${id}`);
    }
  };

  if (loading) {
    return <CourseDetailSkeleton />;
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Course Not Found
          </h1>
          <p className="text-gray-600 mb-8">{error || "Course not found"}</p>
          <Link
            to="/courses"
            className="bg-cyan-600 text-white px-6 py-3 rounded-lg hover:bg-cyan-700 transition-colors"
          >
            Back to Courses
          </Link>
        </div>
      </div>
    );
  }

  const InfoCard: React.FC<{ title: string; children: React.ReactNode }> = ({
    title,
    children,
  }) => (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-200">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
        {title}
      </h3>
      {children}
    </div>
  );

  const DetailRow: React.FC<{ label: string; value: string | null }> = ({
    label,
    value,
  }) => (
    <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-gray-100 last:border-b-0 space-y-1 sm:space-y-0">
      <span className="text-gray-600 font-medium text-sm sm:text-base">
        {label}:
      </span>
      <span className="text-gray-900 text-sm sm:text-base sm:text-right sm:max-w-xs break-words">
        {value || "Not specified"}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link
                  to="/"
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Home
                </Link>
              </li>
              <li>
                <span className="text-gray-500">/</span>
              </li>
              <li>
                <Link
                  to="/courses"
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Courses
                </Link>
              </li>
              <li>
                <span className="text-gray-500">/</span>
              </li>
              <li>
                <span className="text-gray-900 font-medium">
                  {course.title}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Mobile: Course Header with Title and Pricing */}
        <div className="lg:hidden mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <h1 className="text-xl font-bold text-gray-900 mb-2 leading-tight">
              {course.title}
            </h1>
            <div className="flex flex-col space-y-2 text-xs text-gray-600 mb-4">
              <span className="flex items-center">
                <svg
                  className="w-4 h-4 mr-1 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="truncate">{course.university.name}</span>
              </span>
              <div className="flex items-center space-x-4">
                <span className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-1 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {course.duration} years
                </span>
                <span className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-1 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {course.level}
                </span>
              </div>
            </div>
            <div className="text-center p-4 bg-gradient-to-r from-cyan-50 to-orange-50 rounded-lg border border-cyan-200">
              <div className="text-lg font-bold text-cyan-600 mb-1">
                {course.tuitionFeeUSD}
              </div>
              <div className="text-xs text-gray-600">
                {course.tuitionFeeKRW}
              </div>
            </div>
            {course.overview && (
              <div className="mt-4">
                <h3 className="text-base font-semibold text-gray-900 mb-2">
                  Overview
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {course.overview}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Main Content - Mobile: Sequential layout, Desktop: Left column */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Desktop: Course Header (hidden on mobile) */}
            <div className="hidden lg:block bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-4 space-y-4 sm:space-y-0">
                <div className="flex-1">
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 leading-tight">
                    {course.title}
                  </h1>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0 text-xs sm:text-sm text-gray-600">
                    <span className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-1 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="truncate">{course.university.name}</span>
                    </span>
                    <span className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-1 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {course.duration} years
                    </span>
                    <span className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-1 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {course.level}
                    </span>
                  </div>
                </div>
                <div className="text-center sm:text-right flex-shrink-0">
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-cyan-600 mb-1">
                    {course.tuitionFeeUSD}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">
                    {course.tuitionFeeKRW}
                  </div>
                </div>
              </div>

              {course.overview && (
                <div className="mt-4 sm:mt-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">
                    Overview
                  </h3>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    {course.overview}
                  </p>
                </div>
              )}
            </div>

            {/* Mobile Course Information (shown on mobile, hidden on desktop in sidebar) */}
            <div className="lg:hidden">
              <InfoCard title="Course Information">
                <div className="space-y-3">
                  <DetailRow label="Degree" value={course.degree} />
                  <DetailRow label="Level" value={course.level} />
                  <DetailRow
                    label="Duration"
                    value={`${course.duration} years`}
                  />
                  <DetailRow
                    label="Study Mode"
                    value={course.studyMode.replace("_", " ")}
                  />
                  <DetailRow label="Language" value={course.language} />
                  <DetailRow label="Course Code" value={course.courseCode} />
                </div>
              </InfoCard>
            </div>

            {/* Mobile University Info (shown on mobile, hidden on desktop in sidebar) */}
            <div className="lg:hidden">
              <InfoCard title="University">
                <div className="space-y-3">
                  <DetailRow label="Name" value={course.university.name} />
                  <DetailRow
                    label="Location"
                    value={`${course.university.city}, ${course.university.country}`}
                  />
                </div>
              </InfoCard>
            </div>

            {/* Program Structure */}
            {course.programStructure && (
              <InfoCard title="Program Structure">
                <div className="prose prose-gray max-w-none prose-sm sm:prose-base">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {course.programStructure}
                  </p>
                </div>
              </InfoCard>
            )}

            {/* Career Opportunities */}
            {course.careerOpportunities && (
              <InfoCard title="Career Opportunities">
                <div className="prose prose-gray max-w-none prose-sm sm:prose-base">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {course.careerOpportunities}
                  </p>
                </div>
              </InfoCard>
            )}

            {/* Requirements */}
            <InfoCard title="Requirements">
              <div className="space-y-4">
                {course.eligibility && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">
                      Eligibility
                    </h4>
                    <p className="text-sm sm:text-base text-gray-700 whitespace-pre-line">
                      {course.eligibility}
                    </p>
                  </div>
                )}
                {course.requirements && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">
                      Required Documents
                    </h4>
                    <p className="text-sm sm:text-base text-gray-700 whitespace-pre-line">
                      {course.requirements}
                    </p>
                  </div>
                )}
              </div>
            </InfoCard>

            {/* Fees & Costs - Moved to main content */}
            <InfoCard title="Fees & Costs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <DetailRow
                    label="Tuition Fee (USD) per semester"
                    value={course.tuitionFeeUSD}
                  />
                  <DetailRow
                    label="Tuition Fee (KRW) per semester"
                    value={course.tuitionFeeKRW}
                  />
                  {/* {course.applicationFee && (
                    <DetailRow
                      label="Application Fee"
                      value={course.applicationFee}
                    />
                  )} */}
                </div>
                <div className="space-y-3">
                  {course.onlineApplicationFee && (
                    <DetailRow
                      label="Online Application Fee"
                      value={course.onlineApplicationFee}
                    />
                  )}
                  {course.matriculationFee && (
                    <DetailRow
                      label="Matriculation Fee"
                      value={course.matriculationFee}
                    />
                  )}
                  {course.dormitoryFee && (
                    <DetailRow
                      label="Dormitory Fee"
                      value={course.dormitoryFee}
                    />
                  )}
                  {course.bankStatementRequirement && (
                    <DetailRow
                      label="Bank Statement"
                      value={course.bankStatementRequirement}
                    />
                  )}
                </div>
              </div>
            </InfoCard>

            {/* Scholarships - Improved parsing and UX */}
            {(course.firstSemesterScholarship ||
              course.scholarshipFrom2ndSemester) && (
                <InfoCard title="Scholarships & Financial Aid">
                  <div className="space-y-6">
                    {/* First Semester Scholarships */}
                    {course.firstSemesterScholarship && (
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 sm:p-6 rounded-lg border border-green-200">
                        <h4 className="font-semibold text-green-800 mb-3 sm:mb-4 flex items-center text-base sm:text-lg">
                          <svg
                            className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          First Semester Scholarships
                        </h4>
                        <div className="space-y-2">
                          {course.firstSemesterScholarship
                            .split(/[\n]+/)
                            .map((line, idx) => {
                              // Match lines like "IELTS 5.5 / iBT TOEFL 80 = 20%"
                              const match = line.match(
                                /(IELTS\s*[\d.]+.*?TOEFL\s*\d+)\s*=\s*(\d+%)/i
                              );
                              if (match) {
                                return (
                                  <div
                                    key={idx}
                                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white p-3 rounded border border-green-100 space-y-2 sm:space-y-0"
                                  >
                                    <span className="text-gray-700 text-xs sm:text-sm">
                                      <span className="font-medium">
                                        Requirement:
                                      </span>{" "}
                                      {match[1]}
                                    </span>
                                    <span className="text-green-700 font-bold text-sm sm:text-lg">
                                      {match[2]} Tuition Discount
                                    </span>
                                  </div>
                                );
                              }
                              // Match lines like "IELTS 6.0 / iBT TOEFL 85 = 30%"
                              const altMatch = line.match(
                                /(IELTS\s*[\d.]+|iBT\s*TOEFL\s*\d+)\s*=\s*(\d+%)/i
                              );
                              if (altMatch) {
                                return (
                                  <div
                                    key={idx}
                                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white p-3 rounded border border-green-100 space-y-2 sm:space-y-0"
                                  >
                                    <span className="text-gray-700 text-xs sm:text-sm">
                                      <span className="font-medium">
                                        Requirement:
                                      </span>{" "}
                                      {altMatch[1]}
                                    </span>
                                    <span className="text-green-700 font-bold text-sm sm:text-lg">
                                      {altMatch[2]} Tuition Discount
                                    </span>
                                  </div>
                                );
                              }
                              // Fallback for any other line
                              return (
                                <div
                                  key={idx}
                                  className="text-gray-700 text-xs sm:text-sm"
                                >
                                  {line}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    {/* Continuing Scholarships */}
                    {course.scholarshipFrom2ndSemester && (
                      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 sm:p-6 rounded-lg border border-blue-200">
                        <h4 className="font-semibold text-blue-800 mb-3 sm:mb-4 flex items-center text-base sm:text-lg">
                          <svg
                            className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="break-words">
                            Continuing Scholarships (2nd Semester Onwards)
                          </span>
                        </h4>
                        <div className="space-y-2">
                          {course.scholarshipFrom2ndSemester
                            .split(/[\n]+|(?<=%)(?=\s*IELTS)/)
                            .map((line, idx) => {
                              const match = line.match(
                                /(IELTS\s*[\d.]+.*?TOEFL\s*\d+)\s*=\s*(\d+%)/i
                              );
                              if (match) {
                                return (
                                  <div
                                    key={idx}
                                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white p-3 rounded border border-blue-100 space-y-2 sm:space-y-0"
                                  >
                                    <span className="text-gray-700 text-xs sm:text-sm">
                                      <span className="font-medium">
                                        Requirement:
                                      </span>{" "}
                                      {match[1]}
                                    </span>
                                    <span className="text-blue-700 font-bold text-sm sm:text-lg">
                                      {match[2]} Tuition Discount
                                    </span>
                                  </div>
                                );
                              }
                              const altMatch = line.match(
                                /(IELTS\s*[\d.]+|iBT\s*TOEFL\s*\d+)\s*=\s*(\d+%)/i
                              );
                              if (altMatch) {
                                return (
                                  <div
                                    key={idx}
                                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white p-3 rounded border border-blue-100 space-y-2 sm:space-y-0"
                                  >
                                    <span className="text-gray-700 text-xs sm:text-sm">
                                      <span className="font-medium">
                                        Requirement:
                                      </span>{" "}
                                      {altMatch[1]}
                                    </span>
                                    <span className="text-blue-700 font-bold text-sm sm:text-lg">
                                      {altMatch[2]} Tuition Discount
                                    </span>
                                  </div>
                                );
                              }
                              return (
                                <div
                                  key={idx}
                                  className="text-gray-700 text-xs sm:text-sm"
                                >
                                  {line}
                                </div>
                              );
                            })}
                        </div>
                        <div className="mt-4 text-xs sm:text-sm text-blue-600 bg-blue-50 p-3 rounded-md border border-blue-100">
                          <span className="font-medium">📊 Note:</span>{" "}
                          Scholarship percentage based on maintaining language
                          proficiency and academic performance from 2nd semester
                          onwards
                        </div>
                      </div>
                    )}

                    {/* Scholarship Benefits Summary */}
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 sm:p-6 rounded-lg border border-amber-200">
                      <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-3">
                        <svg
                          className="w-6 h-6 text-amber-600 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <div className="flex-1">
                          <h5 className="font-semibold text-amber-800 mb-3 text-base sm:text-lg">
                            💡 Scholarship Benefits & Requirements
                          </h5>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
                            <div>
                              <h6 className="font-medium text-amber-800 mb-2">
                                ✅ Benefits:
                              </h6>
                              <ul className="text-amber-700 space-y-1">
                                <li>• Direct tuition fee reduction</li>
                                <li>• No separate application required</li>
                                <li>
                                  • Automatic assessment based on language scores
                                </li>
                                <li>• Renewable based on performance</li>
                              </ul>
                            </div>
                            <div>
                              <h6 className="font-medium text-amber-800 mb-2">
                                📋 Requirements:
                              </h6>
                              <ul className="text-amber-700 space-y-1">
                                <li>• Valid IELTS or TOEFL scores</li>
                                <li>• Maintain academic standards</li>
                                <li>• Full-time enrollment required</li>
                                <li>• Regular attendance mandatory</li>
                              </ul>
                            </div>
                          </div>
                          <div className="mt-4 p-3 bg-amber-100 rounded-md">
                            <p className="text-amber-800 text-xs sm:text-sm">
                              <span className="font-medium">💡 Pro Tip:</span>{" "}
                              Higher language proficiency scores can save you
                              thousands of dollars throughout your studies.
                              Consider investing in language preparation before
                              applying.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </InfoCard>
              )}

            {/* Mobile: Apply Button (shown after all course content on mobile) */}
            <div className="lg:hidden bg-gradient-to-r from-cyan-600 to-orange-500 rounded-lg p-4 text-white">
              <h3 className="text-base font-semibold mb-2">Ready to Apply?</h3>
              <p className="text-xs mb-4 opacity-90">
                {isAuthenticated
                  ? "Start your application for this program today."
                  : "Sign in to start your application for this program."}
              </p>
              <button
                onClick={handleApplyClick}
                className="w-full bg-white text-cyan-600 py-2 px-4 rounded-md font-medium hover:bg-gray-50 transition-colors text-sm"
              >
                {isAuthenticated ? "Apply Now" : "Sign In to Apply"}
              </button>
            </div>
          </div>

          {/* Desktop Sidebar (hidden on mobile) */}
          <div className="hidden lg:block space-y-6">
            {/* Desktop: Apply Button */}
            <div className="bg-gradient-to-r from-cyan-600 to-orange-500 rounded-lg p-6 text-white">
              <h3 className="text-lg font-semibold mb-2">Ready to Apply?</h3>
              <p className="text-sm mb-4 opacity-90">
                {isAuthenticated
                  ? "Start your application for this program today."
                  : "Sign in to start your application for this program."}
              </p>
              <button
                onClick={handleApplyClick}
                className="w-full bg-white text-cyan-600 py-2 px-4 rounded-md font-medium hover:bg-gray-50 transition-colors"
              >
                {isAuthenticated ? "Apply Now" : "Sign In to Apply"}
              </button>
            </div>

            {/* Desktop: Course Info */}
            <InfoCard title="Course Information">
              <div className="space-y-3">
                <DetailRow label="Degree" value={course.degree} />
                <DetailRow label="Level" value={course.level} />
                <DetailRow
                  label="Duration"
                  value={`${course.duration} years`}
                />
                <DetailRow
                  label="Study Mode"
                  value={course.studyMode.replace("_", " ")}
                />
                <DetailRow label="Language" value={course.language} />
                <DetailRow label="Course Code" value={course.courseCode} />
              </div>
            </InfoCard>

            {/* Desktop: University Info */}
            <InfoCard title="University">
              <div className="space-y-3">
                <DetailRow label="Name" value={course.university.name} />
                <DetailRow
                  label="Location"
                  value={`${course.university.city}, ${course.university.country}`}
                />
              </div>
            </InfoCard>
          </div>
        </div>

        {/* Related Courses */}
        {relatedCourses.length > 0 && (
          <div className="mt-8 sm:mt-12">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                Related Courses
              </h2>
              <p className="text-sm sm:text-base text-gray-600">
                Other programs that might interest you
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {relatedCourses.slice(0, 6).map((relatedCourse) => (
                <CourseCard key={relatedCourse.id} course={relatedCourse} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseDetail;
